import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  FileType2,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Eye,
  Trash2,
  Copy,
  RefreshCw,
  Save,
  X,
  FileText,
  Code,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  inactive: { label: 'Inactive', color: 'bg-slate-500/10 text-slate-400 border-slate-500/30' },
  draft: { label: 'Draft', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  archived: { label: 'Archived', color: 'bg-slate-600/10 text-slate-500 border-slate-600/30' },
};

const TEMPLATE_TYPES = [
  { value: 'dispute_letter', label: 'Dispute Letter' },
  { value: 'bureau_specific', label: 'Bureau Specific' },
  { value: 'consumer_format', label: 'Consumer Format' },
  { value: 'specialist_format', label: 'Specialist Format' },
  { value: 'escalation', label: 'Escalation' },
  { value: 'response', label: 'Response' },
];

const BUREAUS = ['equifax', 'experian', 'transunion'];

const TemplateCard = ({ template, onEdit, onDelete, onPreview, onDuplicate }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div 
      className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all"
      data-testid={`template-card-${template.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-600/20 flex items-center justify-center">
            <FileType2 className="text-teal-400" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{template.name}</h3>
            <p className="text-xs text-slate-500 capitalize">{template.template_type?.replace('_', ' ')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_CONFIG[template.status]?.color}`}>
            {STATUS_CONFIG[template.status]?.label || template.status}
          </span>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-stone-800 transition-all"
            >
              <MoreVertical size={16} />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-36 bg-stone-800 border border-slate-700 rounded-lg shadow-xl z-10 py-1">
                <button
                  onClick={() => { onPreview(template); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  <Eye size={14} /> Preview
                </button>
                <button
                  onClick={() => { onEdit(template); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  <Edit size={14} /> Edit
                </button>
                <button
                  onClick={() => { onDuplicate(template); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  <Copy size={14} /> Duplicate
                </button>
                <button
                  onClick={() => { onDelete(template); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-slate-700"
                >
                  <Trash2 size={14} /> Archive
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {template.description && (
        <p className="text-sm text-slate-400 mb-3 line-clamp-2">{template.description}</p>
      )}

      {/* Bureaus */}
      <div className="flex flex-wrap gap-1 mb-3">
        {(template.bureau_applicability || []).map(bureau => (
          <span key={bureau} className="px-2 py-0.5 text-xs bg-stone-800 text-slate-400 rounded capitalize">
            {bureau}
          </span>
        ))}
      </div>

      {/* Placeholders Preview */}
      {(template.placeholders || []).length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-slate-500 mb-1">Placeholders</p>
          <div className="flex flex-wrap gap-1">
            {template.placeholders.slice(0, 4).map(ph => (
              <span key={ph} className="px-1.5 py-0.5 text-xs font-mono bg-amber-500/10 text-amber-300 rounded">
                {`{{${ph}}}`}
              </span>
            ))}
            {template.placeholders.length > 4 && (
              <span className="px-1.5 py-0.5 text-xs bg-stone-800 text-slate-500 rounded">
                +{template.placeholders.length - 4} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-stone-800">
        <span className="text-xs text-slate-500">{template.content?.length || 0} chars</span>
        <span className="text-xs text-slate-600">v{template.version || 1}</span>
      </div>
    </div>
  );
};

const TemplateEditor = ({ template, onSave, onClose, isNew }) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    template_type: template?.template_type || 'dispute_letter',
    description: template?.description || '',
    bureau_applicability: template?.bureau_applicability || ['equifax', 'experian', 'transunion'],
    content: template?.content || '',
    placeholders: template?.placeholders || [],
    status: template?.status || 'draft',
    notes: template?.notes || '',
    linked_method_id: template?.linked_method_id || '',
  });
  const [saving, setSaving] = useState(false);
  const [placeholderInput, setPlaceholderInput] = useState('');

  const toggleBureau = (bureau) => {
    setFormData(prev => ({
      ...prev,
      bureau_applicability: prev.bureau_applicability.includes(bureau)
        ? prev.bureau_applicability.filter(b => b !== bureau)
        : [...prev.bureau_applicability, bureau],
    }));
  };

  const addPlaceholder = () => {
    if (placeholderInput && !formData.placeholders.includes(placeholderInput)) {
      setFormData(prev => ({
        ...prev,
        placeholders: [...prev.placeholders, placeholderInput],
      }));
      setPlaceholderInput('');
    }
  };

  const removePlaceholder = (ph) => {
    setFormData(prev => ({
      ...prev,
      placeholders: prev.placeholders.filter(p => p !== ph),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.content) {
      toast.error('Name and content are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
      toast.success(isNew ? 'Template created successfully' : 'Template updated successfully');
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-4xl bg-stone-900 border-stone-800 text-white max-h-[90vh]">
      <DialogHeader>
        <DialogTitle>{isNew ? 'Create Template' : 'Edit Template'}</DialogTitle>
        <DialogDescription className="text-slate-400">
          Define output templates for dispute letters and responses.
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-6 max-h-[65vh] overflow-y-auto py-4">
        {/* Left Column - Settings */}
        <div className="space-y-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Template Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Standard Bureau Dispute"
              className="bg-stone-800 border-slate-700 text-white"
            />
          </div>

          <div>
            <label className="text-sm text-slate-400 mb-1 block">Template Type</label>
            <Select
              value={formData.template_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, template_type: value }))}
            >
              <SelectTrigger className="bg-stone-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-stone-800 border-slate-700">
                {TEMPLATE_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-slate-400 mb-1 block">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe when to use this template..."
              className="bg-stone-800 border-slate-700 text-white resize-none"
              rows={2}
            />
          </div>

          <div>
            <label className="text-sm text-slate-400 mb-1 block">Status</label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger className="bg-stone-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-stone-800 border-slate-700">
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-slate-400 mb-2 block">Bureau Applicability</label>
            <div className="flex gap-2">
              {BUREAUS.map(bureau => (
                <button
                  key={bureau}
                  onClick={() => toggleBureau(bureau)}
                  className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-all ${
                    formData.bureau_applicability.includes(bureau)
                      ? 'bg-teal-600 text-white'
                      : 'bg-stone-800 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  {bureau}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-400 mb-1 block">Placeholders</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={placeholderInput}
                onChange={(e) => setPlaceholderInput(e.target.value)}
                placeholder="consumer_name"
                className="bg-stone-800 border-slate-700 text-white text-sm"
                onKeyPress={(e) => e.key === 'Enter' && addPlaceholder()}
              />
              <Button size="sm" variant="outline" onClick={addPlaceholder}>Add</Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {formData.placeholders.map(ph => (
                <span key={ph} className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-amber-500/10 text-amber-300 rounded">
                  {`{{${ph}}}`}
                  <button onClick={() => removePlaceholder(ph)} className="hover:text-red-400">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Content */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-slate-400">Template Content *</label>
            <span className="text-xs text-slate-500">{formData.content.length} chars</span>
          </div>
          <Textarea
            value={formData.content}
            onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
            placeholder="Enter template content with {{placeholders}}..."
            className="bg-stone-800 border-slate-700 text-white font-mono text-sm resize-none h-[400px]"
          />
          <p className="text-xs text-slate-500 mt-1">
            Use {"{{placeholder}}"} syntax for dynamic values. Supports Jinja2 formatting.
          </p>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
          {isNew ? 'Create Template' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const TemplatePreview = ({ template, onClose }) => (
  <DialogContent className="max-w-3xl bg-stone-900 border-stone-800 text-white max-h-[85vh]">
    <DialogHeader>
      <DialogTitle className="flex items-center gap-2">
        <Eye size={20} className="text-teal-400" />
        {template.name}
      </DialogTitle>
      <DialogDescription className="text-slate-400">
        Template preview - {template.template_type?.replace('_', ' ')}
      </DialogDescription>
    </DialogHeader>
    <div className="bg-stone-950 rounded-lg p-4 max-h-[60vh] overflow-y-auto">
      <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono">
        {template.content || 'No content'}
      </pre>
    </div>
    <DialogFooter>
      <Button variant="outline" onClick={onClose}>Close</Button>
    </DialogFooter>
  </DialogContent>
);

const TemplateManagerPage = () => {
  const { getAuthHeaders } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewingTemplate, setPreviewingTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const params = {};
      if (typeFilter !== 'all') params.template_type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const data = await adminOpsService.listTemplates(params);
      setTemplates(data.templates || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load templates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, typeFilter, statusFilter]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSave = async (formData) => {
    adminOpsService.setAuthHeaders(getAuthHeaders());
    if (editingTemplate) {
      await adminOpsService.updateTemplate(editingTemplate.id, formData);
    } else {
      await adminOpsService.createTemplate(formData);
    }
    fetchTemplates();
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Are you sure you want to archive "${template.name}"?`)) return;
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      await adminOpsService.updateTemplate(template.id, { status: 'archived' });
      toast.success('Template archived');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to archive template');
    }
  };

  const handleDuplicate = (template) => {
    setEditingTemplate(null);
    setIsCreating(true);
    setTimeout(() => {
      setEditingTemplate({
        ...template,
        id: null,
        name: `${template.name} (Copy)`,
        status: 'draft',
      });
    }, 0);
  };

  const filteredTemplates = templates.filter(template =>
    template.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="template-manager-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileType2 className="text-teal-400" size={28} />
            Template Manager
          </h1>
          <p className="text-slate-500 mt-1">
            Manage output templates for dispute letters and responses
          </p>
        </div>
        <Button onClick={() => { setEditingTemplate(null); setIsCreating(true); }}>
          <Plus size={18} className="mr-2" />
          New Template
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="pl-10 bg-stone-900 border-stone-800 text-white placeholder:text-slate-500"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44 bg-stone-900 border-stone-800 text-white">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-stone-800 border-slate-700">
            <SelectItem value="all">All Types</SelectItem>
            {TEMPLATE_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 bg-stone-900 border-stone-800 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-stone-800 border-slate-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={fetchTemplates}>
          <RefreshCw size={18} />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Total Templates</p>
          <p className="text-2xl font-bold text-white">{total}</p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Active</p>
          <p className="text-2xl font-bold text-emerald-400">
            {templates.filter(t => t.status === 'active').length}
          </p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Dispute Letters</p>
          <p className="text-2xl font-bold text-teal-400">
            {templates.filter(t => t.template_type === 'dispute_letter').length}
          </p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Draft</p>
          <p className="text-2xl font-bold text-amber-400">
            {templates.filter(t => t.status === 'draft').length}
          </p>
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-56 bg-stone-800" />)}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-16 bg-stone-900 rounded-xl border border-stone-800">
          <FileType2 className="mx-auto text-slate-600 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white mb-2">No Templates Found</h3>
          <p className="text-slate-500 mb-4">
            {searchQuery ? 'No templates match your search.' : 'Create your first template to get started.'}
          </p>
          <Button onClick={() => { setEditingTemplate(null); setIsCreating(true); }}>
            <Plus size={18} className="mr-2" />
            Create Template
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={setEditingTemplate}
              onDelete={handleDelete}
              onPreview={setPreviewingTemplate}
              onDuplicate={handleDuplicate}
            />
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={isCreating || !!editingTemplate} onOpenChange={(open) => { if (!open) { setIsCreating(false); setEditingTemplate(null); } }}>
        <TemplateEditor
          template={editingTemplate}
          onSave={handleSave}
          onClose={() => { setIsCreating(false); setEditingTemplate(null); }}
          isNew={isCreating && !editingTemplate?.id}
        />
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewingTemplate} onOpenChange={(open) => { if (!open) setPreviewingTemplate(null); }}>
        {previewingTemplate && (
          <TemplatePreview
            template={previewingTemplate}
            onClose={() => setPreviewingTemplate(null)}
          />
        )}
      </Dialog>
    </div>
  );
};

export default TemplateManagerPage;
